// Investor types for the admin "add Hub member" flow.
// Mirror the backend contract of GET /investors and POST /investors/:id/members/create-user.

/** Investor as returned by the admin-only listing (GET /investors). */
export interface Investor {
  id: string;
  name: string;
  slug: string;
}

/** Investor-scoped role — distinct from the platform-wide user role. */
export type InvestorMemberRole = 'SUPERADMIN' | 'CALIFICADOR' | 'VISUALIZADOR';

/** Ordered list backing the role selector. */
export const INVESTOR_MEMBER_ROLES: readonly InvestorMemberRole[] = [
  'SUPERADMIN',
  'CALIFICADOR',
  'VISUALIZADOR',
];

/**
 * Body for creating a brand-new user as a member of an existing investor.
 * `password` omitted → the backend sends a magic-link welcome email instead.
 */
export interface CreateHubMemberPayload {
  email: string;
  name: string;
  lastname: string;
  role: InvestorMemberRole;
  password?: string;
}

/** Response of POST /investors/:id/members/create-user. */
export interface HubMemberCreated {
  userId: string;
  email: string;
  memberId: string;
  role: InvestorMemberRole;
}

/** Body of POST /investors — the investor plus its first SUPERADMIN (password required). */
export interface CreateInvestorPayload {
  name: string;
  slug: string;
  superadmin: {
    email: string;
    name: string;
    lastname: string;
    password: string;
  };
}

/** Response of POST /investors. */
export interface InvestorCreated {
  investor: Investor;
  superadmin: HubMemberCreated;
}

/** Platform-wide role, as returned by the membership endpoint. */
export type PlatformUserRole = 'USER' | 'ADMIN' | 'INVESTOR_HUB';

/** GET /investors/memberships/users/:userId — facts needed to warn before a change. */
export interface UserInvestorMembership {
  userId: string;
  userRole: PlatformUserRole;
  membership: {
    investorId: string;
    investorName: string;
    investorSlug: string;
    role: InvestorMemberRole;
  } | null;
  /** True when the user is the only SUPERADMIN of their current investor. */
  isLastSuperadmin: boolean;
  /** Companies the user belongs to (lose this member if the user is deleted). */
  companies: { id: string; name: string; role: string }[];
}

/** PUT body — `investorId: null` removes the user's investor. */
export type SetUserInvestorMembershipPayload =
  | { investorId: string; role: InvestorMemberRole }
  | { investorId: null };

/** PUT response — `userDeleted` when removing the investor deleted a Hub-only user. */
export interface SetUserInvestorMembershipResult {
  userDeleted: boolean;
  membership: UserInvestorMembership | null;
}
